/**
 * Where a new video consultation's link comes from, chosen in the create panel (owner, 28 October
 * 2026): Google Meet when the trainer's Google makes one, else a link — the trainer's standing link
 * prefilled, or one typed for this session. The server's precedence is Manual → Meet → Standing →
 * none. Pure; the wire names arrive with the backend contract.
 */

import { checkCallLink, type GoogleMeetSource } from './callLink';

export type CallLinkSourceChoice = 'meet' | 'own';

export interface CallLinkChoiceView {
  /** The Meet / own-link radio, only where Google makes a Meet; otherwise the field alone. */
  readonly sources: readonly CallLinkSourceChoice[] | null;
  readonly defaultSource: CallLinkSourceChoice;
  /** What the field starts with: the standing link, or nothing. */
  readonly prefill: string;
  /** The suggestions under the field, by platform (`shared/lib/applePlatform`). */
  readonly helperKey: string;
  /** Where Google would make it by itself, said where it does not yet. */
  readonly connectHintKey: string | null;
}

export function callLinkHelperKey(apple: boolean): string {
  return apple ? 'web:booking.call.helperApple' : 'web:booking.call.helperOther';
}

/** `null` while the Google connection is still read — nothing rather than a choice that changes. */
export function callLinkChoice(
  google: GoogleMeetSource,
  standingLink: string | null,
  apple: boolean,
): CallLinkChoiceView | null {
  if (google === 'unknown') return null;
  const common = { prefill: standingLink ?? '', helperKey: callLinkHelperKey(apple) };
  if (google === 'connected') {
    return { ...common, sources: ['meet', 'own'], defaultSource: 'meet', connectHintKey: null };
  }
  return {
    ...common,
    sources: null,
    defaultSource: 'own',
    connectHintKey: google === 'off' ? 'web:booking.create.videoCallConnect' : null,
  };
}

export type ChosenCallLink =
  | { readonly kind: 'meet' }
  /** The standing link, untouched: the server copies it, so a later change of it rewrites nothing. */
  | { readonly kind: 'standing' }
  | { readonly kind: 'manual'; readonly link: string }
  | { readonly kind: 'none' }
  /** What was typed cannot be sent; with „Własny link" chosen, an empty field is that too. */
  | { readonly kind: 'invalid' };

export function chosenCallLink(
  view: CallLinkChoiceView,
  source: CallLinkSourceChoice,
  typed: string,
  standingLink: string | null,
): ChosenCallLink {
  if (source === 'meet' && view.sources !== null) return { kind: 'meet' };
  const value = typed.trim();
  if (value === '') return view.sources === null ? { kind: 'none' } : { kind: 'invalid' };
  if (standingLink !== null && value === standingLink.trim()) return { kind: 'standing' };
  const link = checkCallLink(value);
  return link === null ? { kind: 'invalid' } : { kind: 'manual', link };
}
